import { useEffect, useId, useRef, useState, type ChangeEvent } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/Button';
import { Checkbox } from '../../components/Checkbox';
import { RosterPilotLogo } from '../../assets/RosterPilotLogo';
import {
  acceptLegalDocuments,
  LegalAcceptanceNotAllowedError,
  LegalAcceptanceProgramPausedError,
  type LegalAcceptanceAudience,
} from '../../services/legal';
import { cycleTabFocus } from '../../utils/focus-trap';
import type { LegalAcceptanceRequired, LegalDocumentKind } from '../../types/legal';
import './LegalAcceptanceGate.css';

const DOCUMENT_NAMES: Record<LegalDocumentKind, string> = {
  terms: 'Terms of Service',
  privacy: 'Privacy Policy',
};

const VERSION_CHANGED_FALLBACK =
  'These documents changed again while this was open. Please look over the latest version and accept it.';

type PendingDocument = { kind: LegalDocumentKind; name: string; url: string };

const pendingDocuments = (docs: LegalAcceptanceRequired): PendingDocument[] => {
  const pending: PendingDocument[] = [];
  if (docs.terms) pending.push({ kind: 'terms', name: DOCUMENT_NAMES.terms, url: docs.terms.url });
  if (docs.privacy) {
    pending.push({ kind: 'privacy', name: DOCUMENT_NAMES.privacy, url: docs.privacy.url });
  }
  return pending;
};

/** "Terms of Service", or "Terms of Service and Privacy Policy". */
const joinNames = (documents: PendingDocument[]): string =>
  documents.map((document) => document.name).join(' and ');

/**
 * Blocks the app until the user accepts the current Terms of Service and/or Privacy Policy.
 *
 * Rendered by `ProtectedRoute` in place of the app (like the paused screen), so nothing behind it
 * fires requests the API would refuse anyway. It is a modal dialog with no way out except Accept
 * or Log out: Escape does nothing and Tab stays inside.
 *
 * Only the documents still pending are named. If one changes again while the dialog is open the
 * API answers 409 `version_changed` with the new versions; they replace the old ones here, the box
 * is cleared, and the server's message says why.
 *
 * `audience="travelball"` (a travel ball view) accepts for the travel ball identity at the travel
 * ball endpoint. If that program turns out to be paused, `onProgramPaused` hands over to the paused
 * screen, which takes precedence.
 */
export const LegalAcceptanceGate = ({
  required,
  audience = 'college',
  onProgramPaused,
}: {
  required: LegalAcceptanceRequired;
  audience?: LegalAcceptanceAudience;
  onProgramPaused?: (pauseMessage: string | null) => void;
}) => {
  const { logout, loading: signingOut, refreshProfile } = useAuth();
  const [docs, setDocs] = useState(required);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  const documents = pendingDocuments(docs);
  const names = joinNames(documents);

  // The dialog replaces the whole app, so move focus to its heading on arrival.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // Keep Tab inside the dialog, and refuse Escape: there is nothing behind it to go back to.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.key === 'Tab') cycleTabFocus(dialog, event);
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  const handleAccept = async () => {
    if (!agreed || submitting) return;
    setSubmitting(true);
    setError(null);

    let accepted = false;
    try {
      const result = await acceptLegalDocuments(docs, audience);
      if (result.status === 'version_changed') {
        if (result.legalAcceptanceRequired) {
          setDocs(result.legalAcceptanceRequired);
          setAgreed(false);
          setNotice(result.message ?? VERSION_CHANGED_FALLBACK);
          headingRef.current?.focus();
        } else {
          // Nothing usable came back: ask the profile what is still pending.
          await refreshProfile();
        }
        return;
      }
      accepted = true;
      // The profile now says nothing is pending, and ProtectedRoute renders the app.
      await refreshProfile();
    } catch (caught) {
      if (caught instanceof LegalAcceptanceProgramPausedError && onProgramPaused) {
        onProgramPaused(caught.pauseMessage);
        return;
      }
      if (
        caught instanceof LegalAcceptanceNotAllowedError ||
        caught instanceof LegalAcceptanceProgramPausedError
      ) {
        setError(caught.message);
      } else {
        setError(
          accepted
            ? 'We saved your answer but could not reload your account. Please try again.'
            : 'We could not save that. Check your connection and try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    setError(null);
    try {
      await logout();
    } catch {
      setError('We could not log you out. Please try again.');
    }
  };

  return (
    <div className="legal-gate">
      <main className="legal-gate__container">
        <div className="legal-gate__logo">
          <RosterPilotLogo />
        </div>

        <div
          ref={dialogRef}
          className="legal-gate__card"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          data-testid="legal-acceptance-gate"
        >
          <h1 id={titleId} ref={headingRef} tabIndex={-1} className="legal-gate__title">
            We updated our terms
          </h1>

          <p id={descriptionId} className="legal-gate__text">
            We made changes to our {names}. Please read{' '}
            {documents.length > 1 ? 'them' : 'it'} and accept to keep using RosterPilot.
          </p>

          <ul className="legal-gate__links">
            {documents.map((document) => (
              <li key={document.kind}>
                <a href={document.url} target="_blank" rel="noopener noreferrer">
                  {document.name}
                  <span className="legal-gate__sr-only"> (opens in a new tab)</span>
                </a>
              </li>
            ))}
          </ul>

          {notice && (
            <p className="legal-gate__notice" role="alert" data-testid="legal-gate-notice">
              {notice}
            </p>
          )}

          {error && (
            <p className="legal-gate__error" role="alert" data-testid="legal-gate-error">
              {error}
            </p>
          )}

          <Checkbox
            className="legal-gate__agree"
            checked={agreed}
            disabled={submitting}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setAgreed(event.target.checked)}
            label={`I agree to the ${names}`}
          />

          <div className="legal-gate__actions">
            <Button
              type="button"
              fullWidth
              disabled={!agreed}
              loading={submitting}
              onClick={handleAccept}
            >
              Accept
            </Button>
            <Button
              type="button"
              variant="secondary"
              fullWidth
              loading={signingOut}
              onClick={handleLogout}
            >
              Log out
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};
